import {lazy, Suspense, useContext, useEffect, useState} from 'react'
import {createPortal} from 'react-dom'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {atomIconSizes} from '@s-ui/react-atom-icon'
import {atomToastAutoCloseTimes, atomToastPositions} from '@s-ui/react-atom-toast'
import Context from '@s-ui/react-context'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import IconCircleCheckOutline from '@adv-mt/mt-svg-icons/lib/CircleCheckOutline'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'
import IconInfoOutline from '@adv-mt/mt-svg-icons/lib/InfoOutline'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import ActionSaveNewsButton from './actions-save-news-button.js'

const AtomToast = lazy(() => import(/* webpackChunkName: "AtomToast" */ '@s-ui/react-atom-toast'))

const NOTIFICATION_TYPES = {
  SUCCESS_SAVE: 'SUCCESS_SAVE',
  SUCCESS_DELETE: 'SUCCESS_DELETE',
  ERROR: 'ERROR'
}

const ActionSaveNews = ({newsId, isSaved = false, onClick, pageName, pageType, className, showLabel, size}) => {
  const {domain, i18n} = useContext(Context)
  const {triggerLoginModal} = useContext(LoginModalContext)
  const {track} = useAnalyticsTrack()

  const config = domain.get('config')
  const {USER_NOT_LOGGED_IN_USER_ERROR} = config.get('ERRORS')
  const {TRACKING_EVENT_NAMES, TRACKING_DATA_BY_PAGE, HIT_ORIGINS} = config.get('tracking')
  const {LOGIN_SOURCE_ID_SAVE_NEWS} = config.get('login')
  const isMobile = config.get('isMobile')

  const [isMarked, setIsMarked] = useState(isSaved)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [notification, setNotification] = useState(null)

  useEffect(() => {
    setIsMarked(isSaved)
  }, [isSaved])

  const delayToNextFrame = useDelayToNextFrame()

  const delayedTracking = () => {
    delayToNextFrame(() => {
      track({
        event: TRACKING_EVENT_NAMES.LIST_NEWS_SAVED_CLICKED,
        data: {
          news_id: newsId,
          page_name_origin: pageName,
          page_type: pageType,
          channel: TRACKING_DATA_BY_PAGE.NEWS.channel
        }
      })
    })
  }

  useEffect(() => {
    if (!showLoginModal) return

    triggerLoginModal({
      modalProps: {
        source: LOGIN_SOURCE_ID_SAVE_NEWS,
        trackingOrigin: HIT_ORIGINS.NEWS_SAVED_LIST,
        onLogin: () => {
          domain
            .get('save_news_bookmarks_use_case')
            .execute({articleId: newsId})
            .then(([error]) => {
              if (!error) {
                setIsMarked(true)
                setNotification(NOTIFICATION_TYPES.SUCCESS_SAVE)
                delayedTracking()
              } else {
                setNotification(NOTIFICATION_TYPES.ERROR)
              }
            })
          setShowLoginModal(false)
        },
        onClose: () => {
          setShowLoginModal(false)
        }
      }
    })
  }, [showLoginModal]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = async () => {
    const isSaving = !isMarked
    if (isSaving) delayedTracking()

    const useCase = isMarked ? 'delete_news_bookmarks_use_case' : 'save_news_bookmarks_use_case'
    const [error] = await domain.get(useCase).execute({articleId: newsId})

    if (error instanceof USER_NOT_LOGGED_IN_USER_ERROR) return setShowLoginModal(true)

    if (!error) {
      setIsMarked(isSaving)
      setNotification(isSaving ? NOTIFICATION_TYPES.SUCCESS_SAVE : NOTIFICATION_TYPES.SUCCESS_DELETE)
      onClick?.(isSaving)
    } else {
      setNotification(NOTIFICATION_TYPES.ERROR)
    }
  }

  const NOTIFICATION_LITERALS = {
    [NOTIFICATION_TYPES.SUCCESS_SAVE]: i18n.t('NEWS_BOOKMARKS.SAVE_SUCCESS'),
    [NOTIFICATION_TYPES.SUCCESS_DELETE]: i18n.t('NEWS_BOOKMARKS.DELETE_SUCCESS'),
    [NOTIFICATION_TYPES.ERROR]: i18n.t('NEWS_BOOKMARKS.SAVE_ERROR')
  }

  const isError = notification === NOTIFICATION_TYPES.ERROR

  const notificationClasses = cx('mt-ActionsSaveNews-notification', {
    'mt-ActionsSaveNews-notification--success': !isError,
    'mt-ActionsSaveNews-notification--error': isError
  })

  return (
    <>
      <ActionSaveNewsButton
        isMarked={isMarked}
        onClick={handleToggle}
        className={className}
        showLabel={showLabel}
        size={size}
      />
      {typeof document !== 'undefined' &&
        createPortal(
          <Suspense fallback={null}>
            {notification && (
              <AtomToast
                autoCloseTime={atomToastAutoCloseTimes.short}
                globalClose
                iconClose={<IconClose size={atomIconSizes.medium} />}
                onClose={() => setNotification(null)}
                position={isMobile ? atomToastPositions.top : atomToastPositions.topRight}
                show
              >
                <div className={notificationClasses}>
                  {isError ? (
                    <IconInfoOutline aria-hidden role="presentation" size={atomIconSizes.medium} />
                  ) : (
                    <IconCircleCheckOutline size={atomIconSizes.medium} />
                  )}
                  <p className="mt-ActionsSaveNews-notificationText">{NOTIFICATION_LITERALS[notification]}</p>
                </div>
              </AtomToast>
            )}
          </Suspense>,
          document.body
        )}
    </>
  )
}

ActionSaveNews.displayName = 'ActionSaveNews'
ActionSaveNews.propTypes = {
  className: PropTypes.string,
  isSaved: PropTypes.bool,
  newsId: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  pageName: PropTypes.string,
  pageType: PropTypes.string,
  showLabel: PropTypes.bool,
  size: PropTypes.string
}

export default ActionSaveNews
